import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { svgToBase } from "../components/Icons/layerSvg/clusterAssets";
import { view } from "./API";
import { renderDomain, renderFieldTitle } from "./helper";

const defaultIconSize = 20;
const defaultLineSize = 20;
const defaultColor = "gray";

const fieldDelimiter = ",";

/**
 * Scale presents the zoom level of the map
 * Possible scales: default is the one when user opens the map,
 * Medium is if user zooms in the map in a way that the map becomes 2 times bigger
 * Small is if user zooms in the map in a way that the map becomes 4 times bigger a
 */
const Scale = {
  default: 0,
  medium: 1,
  small: 2,
};

const RendererType = {
  uniqueValue: "unique-value",
  classBreaks: "class-breaks",
};

let controllers = [];
let graphicsLayerUpdateTimer;
const updateLayerViews = async ({
  lv,
  locationsGraphicsLayer,
  newScale,
  symbology,
}) => {
  const graphics = [];
  // const worker = new Worker(new URL("./workers/locations.js", import.meta.url));

  // worker.postMessage(lv);

  // Cancel all previous operations first
  if (controllers.length > 0) {
    controllers.forEach((controller) => {
      controller.abort();
    });
    controllers = [];
  }

  // Create a new controller for this execution
  const mainController = new AbortController();
  controllers.push(mainController);

  try {
    for (const layerView of lv.layerViews) {
      // Check if operation was aborted before continuing
      if (mainController.signal.aborted) {
        throw new DOMException("DOMException Aborted", "AbortError");
      }

      const controller = new AbortController();
      controllers.push(controller);
      const query = layerView.createQuery();
      query.returnGeometry = true;
      query.geometry = view.extent;
      query.spatialRelationship = "contains";

      const { minScale, maxScale } = layerView.layer;

      // Only process this layer if it's visible at the current scale
      const isLayerVisibleAtScale =
        (newScale <= minScale || minScale === 0) &&
        (newScale >= maxScale || maxScale === 0);

      // Skip layer processing entirely if not visible at this scale
      if (!isLayerVisibleAtScale) {
        // console.log(
        //   `Layer "${layerView.layer.title}" not visible at scale ${newScale} (min: ${minScale}, max: ${maxScale}), skipping`
        // );
        continue;
      }
      const results = await layerView.queryFeatures(query, {
        signal: controller.signal,
      });

      // Check if operation was aborted after each async operation
      if (mainController.signal.aborted) {
        throw new DOMException("DOMException Aborted", "AbortError");
      }

      // Convert forEach to a regular for loop to make it easier to handle cancellation
      for (const feature of results.features) {
        // Check if operation was aborted
        if (mainController.signal.aborted) {
          throw new DOMException("DOMException Aborted", "AbortError");
        }

        const { fontSizeMap = { default: {} } } = symbology;
        const sizes = fontSizeMap.default;

        const city = feature.attributes.city_name || "";
        const cityType = feature.attributes.city_type || "";
        const { minScale, maxScale } = layerView.layer;
        const label = `${city}`;

        const point = {
          type: "point",
          x: feature.geometry.x,
          y: feature.geometry.y,
          spatialReference: feature.geometry.spatialReference,
        };

        // Get text symbol style based on city type
        let textSymbol = {
          type: "text",
          color: [104, 104, 104, 255],
          haloColor: "#ffffff",
          haloSize: 1,
          font: {
            family: "Arial",
            size: 7, // default size
            style: "normal",
            weight: "normal",
            decoration: "none",
          },
          verticalAlignment: "bottom",
          horizontalAlignment: "left",
          xoffset: 2,
          yoffset: 2,
        };

        // Match styles based on city type
        switch (cityType) {
          case "national capital":
            textSymbol.color = [78, 78, 78, 255];
            textSymbol.font.size = sizes[cityType] ?? 12;
            textSymbol.font.weight = "bold";
            break;
          case "major city":
          case "city":
            textSymbol.font.size = sizes[cityType] ?? 10;
            if (newScale > 600000) {
              textSymbol.font.size = textSymbol.font.size * 0.8;
            }
            break;
          case "town":
          case "refugee camp":
            textSymbol.font.size = sizes[cityType] ?? 9;
            if (newScale > 600000) {
              textSymbol.font.size = textSymbol.font.size * 0.8;
            }
            break;
          case "locality":
          case "village":
            textSymbol.font.size = sizes[cityType] ?? 8;

            if (newScale > 600000) {
              textSymbol.font.size = textSymbol.font.size * 0.8;
            }
            break;
        }

        textSymbol.text = label;

        const graphic = {
          geometry: point,
          symbol: textSymbol,
          attributes: {
            label,
            cityType,
          },
        };

        graphic.visible =
          (newScale <= minScale || minScale === 0) &&
          (newScale >= maxScale || maxScale === 0);
        graphics.push(graphic);
      }

      // Check for cancellation before sorting
      if (mainController.signal.aborted) {
        throw new DOMException("DOMException Aborted", "AbortError");
      }
    }

    // Final check before modifying the layer
    if (mainController.signal.aborted) {
      throw new DOMException("DOMException Aborted", "AbortError");
    }

    // Sort graphics
    graphics.sort((a, b) => {
      // Final check before modifying the layer

      const fontSizeA = a.symbol.font.size;
      const fontSizeB = b.symbol.font.size;
      return fontSizeA - fontSizeB; // Small fonts first, large fonts last
    });

    // const adjustedGraphics = await positionLabels(graphics, view);
    locationsGraphicsLayer.removeAll();
    requestAnimationFrame(() => {
      if (!mainController.signal.aborted) {
        locationsGraphicsLayer.addMany(graphics);
      }
    });
  } catch (err) {
    console.log(err);

    if (err.name !== "AbortError") {
      console.log(err);
    }
    // Return immediately for aborted operations
    return {
      remove: () => {
        controllers.forEach((controller) => {
          controller.abort();
        });
      },
    };
  }

  return {
    remove: () => {
      controllers.forEach((controller) => {
        controller.abort();
      });
    },
  };
};

// Helper function to get scale ranges
function getScaleRangeForCityType(cityType) {
  switch (cityType) {
    case "national capital":
      return { minScale: 24617456.39825347, maxScale: 0 };
    case "city":
    case "major city":
      return { minScale: 13813411.134440955, maxScale: 0 };
    case "town":
    case "refugee camp":
      return { minScale: 3313440.8789435527, maxScale: 0 };
    case "locality":
      return { minScale: 1130904.823397147, maxScale: 0 };
    case "village":
      return { minScale: 770399.907874108, maxScale: 0 };
    default:
      return { minScale: 0, maxScale: 0 };
  }
}

function getScaleRangeForLayerTitle(title) {
  switch (title) {
    case "National Capital'":
      return { minScale: 24617456.39825347, maxScale: 0 };
    case "Major City and City":
      return { minScale: 13813411.134440955, maxScale: 0 };
    case "Town and Refugee Camp":
      return { minScale: 3313440.8789435527, maxScale: 0 };
    case "Locality":
      return { minScale: 1130904.823397147, maxScale: 0 };
    case "Village":
      return { minScale: 770399.907874108, maxScale: 0 };
    default:
      return { minScale: 0, maxScale: 0 };
  }
}

// Smart label selection function - prioritizes important labels and removes overlaps

const positionLabels = async (graphics, view) => {
  // If there's nothing to position, return immediately
  if (!graphics || !graphics.length) return Promise.resolve(graphics);

  // Return a promise to make this process asynchronous
  return new Promise((resolve) => {
    // Use requestAnimationFrame to avoid blocking the UI thread
    requestAnimationFrame(() => {
      // Define standard offsets for different label types
      const standardOffsets = {
        "national capital": { x: 2, y: 3, vAlign: "right", hAlign: "center" },
        "major city": { x: 2, y: 8, vAlign: "top", hAlign: "center" },
        city: { x: 2, y: 8, vAlign: "top", hAlign: "center" },
        town: { x: 2, y: 4, vAlign: "top", hAlign: "left" },
        "refugee camp": { x: 2, y: 4, vAlign: "top", hAlign: "left" },
        locality: { x: 3, y: 8, vAlign: "top", hAlign: "right" },
        village: { x: 2, y: 8, vAlign: "top", hAlign: "right" },
        default: { x: 1, y: 8, vAlign: "top", hAlign: "right" },
      };

      // 1. Sort graphics by importance
      const sortedGraphics = [...graphics].sort((a, b) => {
        // Primary sort by city type importance
        const cityTypeRank = {
          "national capital": 1,
          "major city": 2,
          city: 3,
          town: 4,
          "refugee camp": 5,
          locality: 6,
          village: 7,
        };

        const rankA = cityTypeRank[a.attributes.cityType] || 99;
        const rankB = cityTypeRank[b.attributes.cityType] || 99;

        if (rankA !== rankB) return rankA - rankB;

        // Secondary sort by font size
        return b.symbol.font.size - a.symbol.font.size;
      });

      // 2. Track placed labels with screen bounds
      const placedLabels = [];

      // 3. Process each graphic in priority order
      sortedGraphics.forEach((graphic) => {
        if (!graphic.visible) return;
        const screenPoint = view.toScreen(graphic.geometry);
        if (!screenPoint) return;

        const fontSize = graphic.symbol.font.size;
        const text = graphic.symbol.text || "";
        const cityType = graphic.attributes.cityType || "default";

        // Get standard offset for this city type
        const offsetConfig =
          standardOffsets[cityType] || standardOffsets.default;

        // Scale offset by font size
        const xOffset = offsetConfig.x * (fontSize / 7);
        const yOffset = offsetConfig.y * (fontSize / 7);

        // Apply offset and alignment
        graphic.symbol = {
          ...graphic.symbol,
          xoffset: xOffset,
          yoffset: yOffset,
          horizontalAlignment: offsetConfig.hAlign,
          verticalAlignment: offsetConfig.vAlign,
        };

        // Calculate bounds for this label
        const bounds = calculateLabelBounds(
          screenPoint.x,
          screenPoint.y,
          xOffset,
          yOffset,
          text,
          fontSize,
          offsetConfig.hAlign,
          offsetConfig.vAlign
        );

        // Check if this label would overlap with already placed labels
        let hasOverlap = false;

        // Major cities and national capitals are always shown
        const isHighPriority =
          cityType === "national capital" || cityType === "major city";

        if (!isHighPriority) {
          for (const placedLabel of placedLabels) {
            if (checkBoundsOverlap(bounds, placedLabel.bounds)) {
              hasOverlap = true;
              break;
            }
          }
        }

        // If no overlap or high priority, add to selected graphics
        if (!hasOverlap || isHighPriority) {
          // graphic.visible = true;
          placedLabels.push({
            bounds,
            graphic,
          });
        } else {
          graphic.symbol.font.size = graphic.symbol.font.size - 2;
          graphic.symbol.verticalAlignment = "bottom";
          graphic.symbol.yoffset = -graphic.symbol.yoffset;

          if (graphic.symbol.horizontalAlignment === "right") {
            graphic.symbol.horizontalAlignment = "left";
            graphic.symbol.xoffset = -graphic.symbol.xoffset;
          }
          // graphic.visible = false;
        }
      });

      resolve(sortedGraphics);
    });
  });
};

// Calculate label bounds
function calculateLabelBounds(
  x,
  y,
  offsetX,
  offsetY,
  text,
  fontSize,
  hAlign,
  vAlign
) {
  // Calculate text dimensions
  const textWidth = text.length * fontSize * 0.6;
  const textHeight = fontSize * 1.2;

  // Apply offsets
  let xMin = x + offsetX;
  let yMin = y + offsetY;

  // Adjust for alignment
  if (hAlign === "center") {
    xMin -= textWidth / 2;
  } else if (hAlign === "right") {
    xMin -= textWidth;
  }

  if (vAlign === "middle") {
    yMin -= textHeight / 2;
  } else if (vAlign === "top") {
    yMin -= textHeight;
  }

  // Add buffer for better spacing
  const buffer = fontSize * 0.5; // Increased buffer for clearer separation

  return {
    xMin: xMin - buffer,
    yMin: yMin - buffer,
    xMax: xMin + textWidth + buffer,
    yMax: yMin + textHeight + buffer,
    width: textWidth + buffer * 2,
    height: textHeight + buffer * 2,
  };
}

// Simple overlap check
function checkBoundsOverlap(a, b) {
  return !(
    a.xMax < b.xMin ||
    a.xMin > b.xMax ||
    a.yMax < b.yMin ||
    a.yMin > b.yMax
  );
}

let layerViewUpdateTimer;
const watchLayerViewUpdate = ({
  locationsGraphicsLayer,
  lv,
  newScale,
  geometryEngine,
  symbology,
}) => {
  let layerViewUpdate;
  let updating;
  clearTimeout(layerViewUpdateTimer);

  let timer = setTimeout(async () => {
    layerViewUpdate = await updateLayerViews({
      lv,
      newScale,
      locationsGraphicsLayer,
      symbology,
    });
  }, 500);

  updating = reactiveUtils.watch(
    () => lv.updating,
    async (isUpdating) => {
      locationsGraphicsLayer.removeAll();
      if (layerViewUpdate) {
        layerViewUpdate.remove();
      }
      clearTimeout(timer);
      if (!isUpdating) {
        layerViewUpdate = await updateLayerViews({
          lv,
          newScale,
          locationsGraphicsLayer,
          symbology,
        });
      }
    }
  );

  return {
    remove: () => {
      if (updating) {
        updating.remove();
      }

      clearTimeout(layerViewUpdateTimer);

      if (layerViewUpdate) {
        layerViewUpdate.remove();
      }
    },
  };
};

const addLocationLabels = async (layer, config, t, symbology) => {
  const locationsGraphicsLayer = new GraphicsLayer();
  locationsGraphicsLayer.title = layer.title + " - Labels";
  locationsGraphicsLayer.id = "locationsGraphicsLayer";
  locationsGraphicsLayer.layerConfigId = layer.layerConfig.id;
  locationsGraphicsLayer.layerConfigAlias = layer.layerConfig.alias;
  locationsGraphicsLayer.layerModules = layer.layerModules;
  // Find the index of the layer with the specified ID
  const targetLayerIndex = view.map.layers.findIndex(
    (l) => l?.layerConfig?.id && l?.layerConfig?.id === layer?.layerConfig?.id
  );
  // Add 1 to place it after the target layer
  const insertIndex = targetLayerIndex !== -1 ? targetLayerIndex + 1 : 0;

  view.map.add(locationsGraphicsLayer, insertIndex);
  const { style, ...rest } = symbology;
  view.whenLayerView(layer).then((lv) => {
    layer.layers.forEach((l) => {
      view
        .whenLayerView(l)
        .then(() => {
          addSymbology(l, config, t, rest);
        })
        .catch((err) => {
          console.log(err);
        });
    });
    if (lv.type === "group") {
      layer.layers.forEach((l) => {
        l.labelsVisible = false;
        // l.renderer = undefined;
      });
      let updating = watchLayerViewUpdate({
        locationsGraphicsLayer,
        lv,
        newScale: view.scale,
        geometryEngine,
        symbology,
      });

      let timer;
      reactiveUtils.watch(
        () => view.scale,
        (newScale) => {
          controllers.forEach((controller) => {
            controller.abort();
          });
          if (updating) {
            updating.remove();
          }
          requestAnimationFrame(() => {
            locationsGraphicsLayer.removeAll();
          });

          clearTimeout(timer);
          timer = setTimeout(() => {
            updating = watchLayerViewUpdate({
              locationsGraphicsLayer,
              lv,
              newScale,
              geometryEngine,
              symbology,
            });
          }, 100);
        }
      );
    }
    // Watch for updates in the layerView
  });
};

export const symbologiesMap = new Map();
export const addSymbology = (
  layer,
  config,
  t,
  selectedSymbology = undefined
) => {
  if (!layer.layerConfig?.symbology) return;

  let symbology = selectedSymbology;
  if (!symbology) {
    symbology = getLayerSymbology(layer, config);
  }

  if (!symbology) {
    console.warn(
      "Unknown symbology. Please create the symbology in the layer Symbologies section in the config.json. Layer: " +
        layer.title
    );
    return;
  }

  if (symbology.style === "text") {
    addLocationLabels(layer, config, t, symbology);
    return;
  }

  const rendererType =
    symbology.type && symbology.type === RendererType.classBreaks
      ? RendererType.classBreaks
      : RendererType.uniqueValue;

  layer.renderer = createValueRenderer(rendererType, layer, symbology, t);

  const initialScale = view.scale;
  let lastUserScale = null;
  reactiveUtils.watch(
    () => view.scale,
    (currentScale) => {
      let mapScale = Scale.default;
      if (currentScale < initialScale / 2) mapScale = Scale.medium;
      if (currentScale < initialScale / 4) mapScale = Scale.small;

      if (!lastUserScale || lastUserScale !== mapScale) {
        lastUserScale = mapScale;
        layer.renderer = createValueRenderer(
          rendererType,
          layer,
          symbology,
          t,
          mapScale
        );
      }
    }
  );
};

export const getLayerIconSymbology = (layer, config) => {
  try {
    let symbology;
    if (!Array.isArray(layer.layerConfig?.symbology)) {
      symbology = layer.layerConfig?.symbology;
    } else if (layer.layerConfig?.symbology.length > 0) {
      symbology = layer.layerConfig?.symbology[0];
    }
    return config.layerSymbologies[symbology];
  } catch (err) {
    return null;
  }
};

export const getLayerSymbology = (layer, config) => {
  try {
    let symbology;
    if (!Array.isArray(layer.layerConfig?.symbology)) {
      symbology = layer.layerConfig?.symbology;
    } else if (layer.layerConfig?.symbology.length > 0) {
      symbology = layer.layerConfig?.symbology[0];
    }

    const { layerIcon, ...layerSymbology } =
      config.layerSymbologies[symbology] || {};

    return Object.keys(layerSymbology).length > 0 ? layerSymbology : null;
  } catch (err) {
    return null;
  }
};

export const defaultMap = new Map();
const createValueRenderer = (rendererType, layer, symbology, t, scale) => {
  if (!scale) scale = Scale.default;

  const symbol = createSymbol(
    symbology,
    layer,
    symbology.defaultIcon,
    symbology.defaultColor,
    symbology.defaultSize
  );

  if (!defaultMap.has(layer.id) && symbol && symbol.type === "picture-marker") {
    defaultMap.set(layer.id, symbol.url);
  }

  let renderer = {
    type: RendererType.uniqueValue,
    fieldDelimiter: fieldDelimiter,
    uniqueValueInfos: [],
    classBreakInfos: [],
    defaultSymbol: symbol,
  };

  let iconValueDefinitions = getIconValueDefinitions(
    symbology,
    layer,
    scale,
    t
  );
  renderer.getIconDefinitions = () => {
    return iconValueDefinitions;
  };
  renderer.getDefaults = () => {
    return {
      icon: symbology.defaultIcon,
      color: symbology.defaultColor,
      size: symbology.defaultSize,
      description: `${symbology.defaultIcon}-${symbology.defaultColor}`,
    };
  };

  const colorValueDefinitions = getColorValueDefinitions(
    rendererType,
    symbology,
    layer,
    scale,
    t
  );
  const markerValueDefinitions = getMarkerValueDefinitions(
    rendererType,
    symbology,
    layer,
    scale,
    t
  );

  renderer.getColorDefinitions = () => {
    return colorValueDefinitions;
  };

  renderer.markerValueDefinitions = () => {
    return markerValueDefinitions;
  };

  let valueDefinitions = [];

  if (
    iconValueDefinitions.length > 0 &&
    colorValueDefinitions.length > 0 &&
    markerValueDefinitions.length > 0
  ) {
    iconValueDefinitions.forEach((iconVd) => {
      colorValueDefinitions.forEach((colorVd) => {
        markerValueDefinitions.forEach((markerVd) => {
          valueDefinitions.push({
            icon: iconVd.icon,
            color: colorVd.color,
            marker: markerVd.marker,
            size: symbology.defaultSize,
            field: [].concat(iconVd.field, colorVd.field, markerVd.field),
            value:
              iconVd.value +
              fieldDelimiter +
              colorVd.value +
              fieldDelimiter +
              markerVd.value,
            label: `${iconVd.label} ${colorVd.label} ${markerVd.label}`,
          });
        });
      });
    });
  } else if (
    iconValueDefinitions.length > 0 &&
    colorValueDefinitions.length > 0
  ) {
    iconValueDefinitions.forEach((iconVd) => {
      colorValueDefinitions.forEach((colorVd) => {
        valueDefinitions.push({
          icon: iconVd.icon,
          color: colorVd.color,
          size: symbology.defaultSize,
          field: [].concat(iconVd.field, colorVd.field),
          value: iconVd.value + fieldDelimiter + colorVd.value,
          label: `${iconVd.label} ${colorVd.label}`,
        });
      });
    });
  } else if (
    iconValueDefinitions.length > 0 &&
    markerValueDefinitions.length > 0
  ) {
    iconValueDefinitions.forEach((iconVd) => {
      markerValueDefinitions.forEach((markerVd) => {
        valueDefinitions.push({
          icon: iconVd.icon,
          marker: markerVd.marker,
          size: symbology.defaultSize,
          field: [].concat(iconVd.field, markerVd.field),
          value: iconVd.value + fieldDelimiter + markerVd.value,
          label: `${iconVd.label} ${markerVd.label}`,
        });
      });
    });
  } else if (
    colorValueDefinitions.length > 0 &&
    markerValueDefinitions.length > 0
  ) {
    colorValueDefinitions.forEach((colorVd) => {
      markerValueDefinitions.forEach((markerVd) => {
        valueDefinitions.push({
          color: colorVd.color,
          marker: markerVd.marker,
          size: symbology.defaultSize,
          field: [].concat(colorVd.field, markerVd.field),
          value: colorVd.value + fieldDelimiter + markerVd.value,
          label: `${colorVd.label} ${markerVd.label}`,
        });
      });
    });
  } else if (iconValueDefinitions.length > 0) {
    valueDefinitions = iconValueDefinitions;
  } else if (colorValueDefinitions.length > 0) {
    valueDefinitions = colorValueDefinitions;
  } else if (markerValueDefinitions.length > 0) {
    valueDefinitions = markerValueDefinitions;
  }

  const sizeMap = symbology.sizeMap;
  let valueExpression = "";
  valueDefinitions.forEach((vd, idx) => {
    if (rendererType === RendererType.uniqueValue) {
      if (sizeMap && vd.field.length > 0) {
        if (sizeMap.default && vd.value in sizeMap.default) {
          vd.size = sizeMap.default[vd.value];
        }
      }

      let expression = createValueExpression(
        symbology,
        layer,
        vd.field,
        vd.value,
        vd.label,
        vd.icon,
        vd.color,
        vd.size,
        vd.marker
      );
      valueExpression += expression[0];
      renderer.uniqueValueInfos.push(expression[1]);
    } else if (rendererType === RendererType.classBreaks) {
      valueExpression += createBetweenExpression(layer, vd, idx);

      const symbol = createSymbol(symbology, layer, vd.icon, vd.color, vd.size);
      renderer.uniqueValueInfos.push({
        value: idx,
        label: `${vd.label}`,
        symbol: symbol,
        description: `${vd.icon}-${vd.color}`,
      });
    }
  });

  if (valueExpression !== "")
    renderer.valueExpression = `WHEN(${valueExpression}'0')`;
  else renderer.valueExpression = `1`;

  renderer.getSizeDefinitions = () => {
    if (!sizeMap) return;

    return { field: sizeMap.field, scale: getScale(sizeMap, scale) };
  };

  if (!symbology.sizeMap && symbology.defaultSize) {
    const defaultSize = symbology.defaultSize;
    const stops = [
      { size: defaultSize, value: 9244648 }, //6 0
      { size: 1, value: 18489297 }, //5 1
      { size: 1, value: 36978595 }, //4 2
      { size: 1, value: 73957190 }, //3 3
      { size: 1, value: 147914381 }, //2 4
      { size: 1, value: 295828763 }, //1 5
    ];

    stops.forEach((stop, index) => {
      if (index === 0) return;
      stop.size =
        stops[index - 1].size - stops[index - 1].size * (0.1 + index / 10);
      if (stop.size < 0.5) {
        stop.size = 0.5;
      }
    });

    const sizeVV = {
      type: "size",
      valueExpression: "$view.scale",
      stops,
    };
    renderer.visualVariables = [sizeVV];
  }

  // if (sizeMap?.field === "city_type") {
  //   const cityTypeSizeVariable = {
  //     type: "size",
  //     // field: "city_type",
  //     // valueType: "string",
  //     valueExpression: "$feature.city_type",
  //     stops: [
  //       { value: "national capital", size: 100 },
  //       { value: "major city", size: 8 },
  //       { value: "city", size: 6 },
  //       { value: "town", size: 3 },
  //       { value: "refugee camp", size: 3 },
  //       { value: "locality", size: 2 },
  //       { value: "village", size: 2 },
  //     ],
  //   };

  //   renderer.visualVariables = [cityTypeSizeVariable];
  // }

  if (sizeMap) {
    const sizeField = layer.fields.filter(
      (field) => field.name === sizeMap.field
    )[0];
    if (!sizeField) {
      console.warn(
        "Symbology warning: sizeMap must refer a valid field. Layer: " +
          layer.title
      );
    } else {
      renderer.visualVariables = [
        getSizeVisualVariables(sizeField, getScale(sizeMap, scale), t),
      ];
    }
  }

  return renderer;
};

export const getIconValueDefinitions = (symbology, layer, scale, t) => {
  const ret = [];
  if (!symbology.iconMap) return ret;

  const fields = symbology.iconMap.field
    ? [symbology.iconMap.field]
    : symbology.iconMap.fields;
  const layerFields = filterFields(layer, fields);
  if (!layerFields) return ret;

  layer.opacity = 1;

  const iconMap = getScale(symbology.iconMap, scale);
  iconMap &&
    Object.keys(iconMap).forEach((value) => {
      const iconName = iconMap[value];
      const iconLabel = renderDomain(layerFields, value.split(","), t);
      ret.push({
        icon: iconName,
        field: layerFields,
        value: value,
        size: symbology.defaultSize,
        label: iconLabel,
        color: symbology.defaultColor,
      });
    });

  return ret;
};

const getColorValueDefinitions = (rendererType, symbology, layer, scale, t) => {
  const ret = [];
  if (!symbology.colorMap) return ret;

  const fields = symbology.colorMap.field
    ? [symbology.colorMap.field]
    : symbology.colorMap.fields;
  const layerFields = filterFields(layer, fields);
  if (!layerFields) return ret;

  const colorMap = getScale(symbology.colorMap, scale);
  if (colorMap) {
    if (rendererType === RendererType.uniqueValue) {
      Object.keys(colorMap).forEach((fieldValue) => {
        const color = colorMap[fieldValue];
        const colorLabel = fieldValue.includes("-")
          ? renderFieldTitle(layerFields[0], t) + ": " + fieldValue
          : renderDomain(layerFields, fieldValue.split(","), t);

        ret.push({
          field: layerFields,
          value: fieldValue,
          size: symbology.defaultSize,
          label: `${colorLabel}`,
          color: color,
          icon: symbology.defaultIcon,
          style: symbology.style,
        });
      });
    } else {
      colorMap.forEach((range) => {
        if (layerFields.length > 0)
          console.warn(
            "The symbology of type 'class-breaks' supports only one field. Layer: " +
              layer.title
          );

        const colorLabel =
          renderFieldTitle(layerFields[0], t) +
          ": " +
          range.minValue +
          " - " +
          range.maxValue;

        ret.push({
          field: layerFields,
          minValue: range.minValue,
          maxValue: range.maxValue,
          size: symbology.defaultSize,
          label: `${colorLabel}`,
          color: range.color,
          icon: symbology.defaultIcon,
          style: symbology.style,
        });
      });
    }
  }

  return ret;
};

const getMarkerValueDefinitions = (
  rendererType,
  symbology,
  layer,
  scale,
  t
) => {
  const ret = [];
  if (!symbology.markerMap) return ret;

  const fields = symbology.markerMap.field
    ? [symbology.markerMap.field]
    : symbology.markerMap.fields;
  const layerFields = filterFields(layer, fields);
  if (!layerFields) return ret;

  const markerMap = getScale(symbology.markerMap, scale);
  if (markerMap) {
    if (rendererType === RendererType.uniqueValue) {
      Object.keys(markerMap).forEach((fieldValue) => {
        const marker = markerMap[fieldValue];
        const markerLabel = renderDomain(layerFields, fieldValue.split(","), t);

        ret.push({
          field: layerFields,
          value: fieldValue,
          size: symbology.defaultSize,
          label: markerLabel,
          marker: marker,
        });
      });
    } else {
      // markerMap.forEach((range) => {
      //   if (layerFields.length > 0)
      //     console.warn(
      //       "The symbology of type 'class-breaks' supports only one field. Layer: " +
      //         layer.title
      //     );
      //   const markerLabel =
      //     renderFieldTitle(layerFields[0], t) +
      //     ": " +
      //     range.minValue +
      //     " - " +
      //     range.maxValue;
      //   ret.push({
      //     field: layerFields,
      //     minValue: range.minValue,
      //     maxValue: range.maxValue,
      //     size: symbology.defaultSize,
      //     label: markerLabel,
      //     marker: range.marker,
      //   });
      // });
    }
  }

  return ret;
};

const filterFields = (layer, fields) => {
  const layerFields =
    fields &&
    fields
      .map((cField) => {
        return layer.fields.filter((field) => field.name === cField)[0];
      })
      .filter((field) => field);
  if (
    !layerFields ||
    layerFields.length === 0 ||
    layerFields.length !== fields.length
  ) {
    console.warn(
      "Symbology warning: symbology must refer valid layer fields. Layer: " +
        layer.title +
        " Fields: " +
        fields
    );
    return;
  }

  return layerFields;
};
/**
 * Get the right scale to use:
 * default scale is taken for the original the zoom level,
 * medium scale is taken when the zoom level is bigger than the double of the original one
 * small scale is taken when the zoom level is 4 times bigger than the original one
 */
const getScale = (map, scale) => {
  if (!map) return;

  if (scale === Scale.small && map.small) return map.small;

  if ((scale === Scale.medium || scale === Scale.small) && map.medium)
    return map.medium;

  return map.default;
};
const getSizeVisualVariables = (sizeField, values, t) => {
  const sizes = {
    type: "size",
    field: sizeField.name,
    valueType: "string",
    stops: [],
  };

  if (!values) return sizes;

  Object.keys(values).forEach((sizeValue) => {
    sizes.stops.push({
      value: sizeValue,
      size: values[sizeValue],
      label: renderDomain(sizeField, sizeValue, t),
    });
  });
  return sizes;
};

const createValueExpression = (
  symbology,
  layer,
  fields,
  fieldValue,
  label,
  iconName,
  color,
  size,
  marker
) => {
  let valueExpression = "";
  let fieldValues = fieldValue.split(fieldDelimiter);
  fieldValues.forEach(function (val, i) {
    if (i >= fields.length) return;

    const fieldName = fields[i].name;
    if (val.toLowerCase() === "null")
      valueExpression += ` IsEmpty($feature.${fieldName}) &&`;
    else if (val.includes("-")) {
      const minMax = fieldValue.split("-");
      valueExpression += ` $feature.${fieldName} >= ${minMax[0]} && $feature.${fieldName} < ${minMax[1]} &&`;
    } else if (val !== "*") {
      valueExpression +=
        fields[i].type === "string"
          ? ` $feature.${fieldName} == '${val}' &&`
          : ` $feature.${fieldName} == ${val} &&`;
    }
  });

  if (valueExpression.endsWith("&&"))
    valueExpression = valueExpression.slice(0, -2).trim();
  const symbol = createSymbol(symbology, layer, iconName, color, size, marker);

  return [
    `${valueExpression},'${fieldValue}',`,
    {
      value: fieldValue,
      label: label,
      symbol: symbol,
      description: `${iconName}-${color}`,
    },
  ];
};

/**
 * Create definition expression for mix and max values based on a specific field value
 */
const createBetweenExpression = (layer, vd, index) => {
  let valueExpressionElements = [];
  if (vd.minValue !== undefined)
    valueExpressionElements.push(
      ` $feature.${vd.field[0].name} >= ${vd.minValue} `
    );

  if (vd.maxValue !== undefined)
    valueExpressionElements.push(
      ` $feature.${vd.field[0].name} < ${vd.maxValue} `
    );

  const valueExpression = valueExpressionConcat(valueExpressionElements, "&&");
  return `${valueExpression},'${index}',`;
};

const valueExpressionConcat = (elements, separator) => {
  let res = "";
  for (const key in elements) {
    const val = elements[key];

    if (!val || val.trim().length === 0) continue;

    if (res.length > 0) res += ` ${separator} `;

    res += val;
  }

  return res;
};

export const symbolMap = new Map();
export const createSymbol = (
  symbology,
  layer,
  iconName,
  color,
  size,
  marker
) => {
  if (!symbolMap.has(`${iconName}-${color}`)) {
    symbolMap.set(
      `${iconName}-${color}`,
      svgToBase(iconName, color, false, (size ? size : defaultIconSize) * 8)
    );
  }

  if (layer.geometryType === "polyline" || layer.geometryType === "line") {
    const symbol = {
      type: "simple-line",
      color: color ? color : defaultColor,
      width: size ? size : defaultLineSize,
      style: symbology?.style || "solid",
    };

    if (marker) {
      symbol.marker = {
        style: "arrow",
        color: color,
        placement: marker,
      };
    }

    return symbol;
  } else if (layer.geometryType === "point")
    return {
      type: "picture-marker",
      url: symbolMap.get(`${iconName}-${color}`),
      height: size ? size : defaultIconSize,
      width: size ? size : defaultIconSize,
    };
  else if (layer.geometryType === "polygon") {
    const outline = {
      width:
        symbology.defaultBorderSize !== undefined
          ? symbology.defaultBorderSize
          : 1,
      color: symbology.defaultBorderColor
        ? symbology.defaultBorderColor
        : "black",
    };

    return {
      type: "simple-fill",
      color: color,
      style: "solid",
      outline: outline,
    };
  }
};
